import { ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, MoreVertical, X } from 'lucide-react';

import { handleModalEscape, pushModalEscape } from '../../../lib/modal-escape';
import { clampInlineEnd, placeBlock, windowViewport } from '../../../lib/popover-placement';
import { Button } from './Button';
import { IconButton } from './IconButton';
import styles from './OverflowMenu.module.css';

export interface OverflowMenuAction {
  key: string;
  /**
   * The item's content. A plain string for almost every caller — and the LABELLED picker trigger still
   * takes a string of its own, because a trigger has to state one value as text.
   *
   * Widened to `ReactNode` for a picker whose values are STATUSES: the Overview home's activity filter
   * lists "Waiting on you / In Progress / Done", and those three are DS `Badge`s everywhere else in
   * the app, so rendering them as plain text here made the same three states look like two different
   * vocabularies one click apart. A Badge states and a menu item acts, so nesting them is not a
   * clickable Badge. `string` still satisfies this, so no existing caller changes.
   */
  label: ReactNode;
  /** Absent when `href` is a plain navigation with nothing else to do (still fired for BI on an href
   *  item too — see the render below). */
  onSelect?: () => void;
  destructive?: boolean;
  /** A leading 16px glyph — optional; the Files-row menu uses these (View/Download/Print/…), the Pack
   *  content-row menu (View / Change in chat / Remove) does not. */
  icon?: ReactNode;
  /** A navigation target instead of (or alongside) a click handler — e.g. "Open in Wixel", or our own
   *  standalone file page.
   *
   *  Rendered as a plain `<a>`, never a same-origin `<Link>`, and that stays true even for our own URLs: an
   *  href here always opens a NEW document (see `newTab`), so Next's client-side routing has nothing to do
   *  and its prefetch would be wasted. Pass an already-resolved, mount-prefixed URL. */
  href?: string;
  /** `href` points OUT of the app. Implies a new tab. */
  external?: boolean;
  /** Opens `href` in a new tab. Independent of `external`, because a SAME-ORIGIN app link can want one too —
   *  which is exactly what "Open in new tab" on a file is: our own viewer page, in a new tab. */
  newTab?: boolean;
}

/** Clear space kept between the menu and the window's edges. */
const EDGE = 8;
/** Gap between the trigger and the menu, on whichever side it opens. */
const GAP = 4;
/** `.menu`'s `min-width` — the width reserved by the FIRST pass, before the menu has rendered and its
 *  real width (a long localized label can far exceed the minimum) can be measured. */
const MIN_WIDTH = 180;

// THE row-overflow menu: a 3-dot trigger holding a row's actions as NAMED items. Which actions are present
// is the caller's decision, per item.
//
// A named item is the point, not decoration. The Files table used to expose its one row action as a lone
// share glyph (an arrow out of a tray), which reads as "upload" — reported from the GA build — and a lone
// glyph has no other way to say what it does. Named items cannot be misread, and the menu gives a row room
// for the actions that had nowhere to live.
//
// Rendered in a PORTAL, positioned under the trigger, so an ancestor's `overflow: hidden` (the section
// card that rounds its rows) can't clip it. Closes on outside-click, Escape, or scroll/resize.
//
// `actions` (a single flat list, no divider) is the original — and still the more common — shape; Packs
// and the Files table both pass it. `groups` (a list of lists) renders a divider BETWEEN groups only —
// none before the first, none after the last — for a caller that needs to set a trailing action (e.g.
// Discuss) apart from the rest, like the doc-viewer file actions do. Passing both is not meaningful;
// `groups` wins if both are given.
//
// ## The labelled variant is a PICKER, not a second row menu
//
// Pass `label` and the trigger becomes a DS outlined Button carrying that text plus a chevron, aligned to its
// own inline-start rather than right-aligned to a row's edge. With `selectedKey` the chosen item wears a
// check. That is what the Files filters are: "Date: Last 7 days" opening a list of buckets — a control whose
// label states the current value, which a 3-dot glyph cannot do.
//
// It lives here rather than as a separate component because everything below the trigger is identical —
// the portal, the placement, the outside-click and the shared Escape-layer handling — and a second copy of
// that is exactly the drift the DS section of CLAUDE.md warns about. A raw `<button>` picker was the other
// option; `test/button-spec.test.ts` rightly forbids one.
export function OverflowMenu({
  actions,
  groups,
  ariaLabel,
  onOpen,
  label,
  labelIcon,
  onClear,
  clearAriaLabel,
  selectedKey,
  className,
  menuClassName,
  triggerClassName,
  triggerIcon,
  triggerSize,
  triggerVariant = 'outlined',
  density = 'default',
  align,
  blockAnchorRef,
  blockAnchorGap = 0,
}: {
  actions?: OverflowMenuAction[];
  groups?: OverflowMenuAction[][];
  ariaLabel: string;
  onOpen?: () => void;
  /** Renders the labelled picker trigger instead of the 3-dot glyph. */
  label?: string;
  /** A leading mark on the LABELLED trigger — the Files Space chip's 20px `WorkspaceBadge`, so a chosen
   *  Space is recognised on the chip the same way it is recognised in the menu below it. */
  labelIcon?: ReactNode;
  /**
   * Resets THIS axis, and only this one. Present → the labelled trigger grows a ✕ between its label and
   * its chevron (Figma: a chip that is actually filtered says so and offers the way out), which is why it
   * is only ever passed by a chip whose value is away from its "All …/Any …" rest.
   *
   * The ✕ is a real, focusable `IconButton` SIBLING of the label button inside one bordered shell — never
   * a control nested in a control, which is invalid HTML and steals the outer button's click.
   */
  onClear?: () => void;
  /** The ✕'s accessible name. Required in practice whenever `onClear` is passed. */
  clearAriaLabel?: string;
  /** The `key` of the currently chosen action — it wears a check. Only meaningful for the picker. */
  selectedKey?: string;
  /** Extra class on the WRAPPER — the box the trigger sits in, which is also the flex item its row
   *  lays out. The Files filter chips use it to share their row's width equally on a phone, where the
   *  wrapper's own `flex-shrink: 0` would otherwise push the row wide. Layout only. */
  className?: string;
  /** Extra class on the popover itself — a call-site override of the menu's own box (padding, radius,
   *  item spacing), for a picker the Figma draws as a roomier card than the default Action Menu. Layout
   *  only; never a colour. */
  menuClassName?: string;
  /** Extra class on the trigger itself — a call-site-specific override (e.g. suppressing hover on a
   *  trigger that sits on its own circular chip), never a colour.
   *
   *  Lands on the OUTERMOST trigger box in all three shapes — the bare icon button, the plain labelled
   *  button, and the ✕-bearing chip's bordered shell — so a call site styling "the chip" gets the same
   *  element whether or not that chip currently carries a value. */
  triggerClassName?: string;
  /** Replaces the default bare-trigger glyph (`MoreVertical`) — e.g. the chat message tool bar's
   *  horizontal `MoreHorizontal`, matching its own 16px icon set instead of this menu's usual 18px
   *  vertical dots. No effect on the labelled picker, which never shows a glyph of its own. */
  triggerIcon?: ReactNode;
  /**
   * The trigger's DS size. Defaults differ by trigger because their frames do: the labelled PICKER is a
   * 36px `medium` chip (Figma — the Files/Tasks/Routines filter rows), the bare 3-dot trigger a 30px
   * `small` one. A row that draws a tighter pair of trailing controls passes `tiny` (26px) — the Files
   * row's Share + ⋮, which the redesign sizes to 26.
   */
  triggerSize?: 'tiny' | 'small' | 'medium' | 'large';
  /**
   * The labelled picker trigger's DS Button skin — `outlined` (the default), `text` (a quiet ghost trigger
   * with no resting border, for a picker that sits on a section header, e.g. the category filters), or
   * `contained` (a menu that is the view's main action, e.g. the Files "Add ⌄"). Only the labelled trigger
   * WITHOUT `onClear` reads it; the ✕-bearing chip is always a text label inside a bordered shell.
   */
  triggerVariant?: 'outlined' | 'text' | 'contained';
  /**
   * How tightly the MENU itself is set. `default` is the DS Action Menu (Figma 14710:128562) — 14/18
   * Medium items on an 8·6 padded card — which Tasks, Routines, Packs, the conversation rows, the chat
   * header and the doc viewer all draw. `compact` is the Files options popover (Figma 15717:140124):
   * 12/16 SemiBold items in a 26px row, 4 apart, on a 12·16 padded card with a lighter two-layer
   * shadow.
   *
   * It is a VARIANT rather than a new default because the Files redesign moved ONE popover, not every
   * menu in the app — repainting `.menu` would silently re-set six other surfaces that were tuned to
   * the Action Menu node in the round before this one.
   */
  density?: 'default' | 'compact';
  /** Which edge of the trigger the menu's own edge aligns to. Defaults to 'start' for the labelled
   *  picker (it opens beside its own leading edge, where the row's label already sits) and 'end' for
   *  the bare trigger (right-aligned to a row's trailing edge, where a 3-dot trigger usually sits).
   *  Pass explicitly when a bare trigger sits at a row's LEADING edge instead — the chat message tool
   *  bar's "more" button is the first icon in its row, not the last. */
  align?: 'start' | 'end';
  /** Open the menu below THIS element's bottom edge instead of the trigger's, flush (no gap). The chat
   *  header passes its own `<header>` so the menu hangs from the header's bottom edge (Figma — the
   *  Action Menu's top sits exactly on the header bottom), independent of how tall the trigger is (a
   *  30px desktop button vs the 36px mobile circle vs the agents-island row would each need a different
   *  trigger-relative gap otherwise). Horizontal alignment stays trigger-based. Unset → the menu opens
   *  below the trigger with the usual gap, unchanged for every row-menu caller. */
  blockAnchorRef?: RefObject<HTMLElement | null>;
  /** Extra px below `blockAnchorRef`'s bottom (default 0 = flush). On a NARROW screen the centered
   *  agents island sits horizontally next to this right-aligned menu, so the mobile chat header adds a
   *  little clearance here; the wide surfaces stay flush. No effect without `blockAnchorRef`. */
  blockAnchorGap?: number;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Right-align the menu to the trigger; open below it by default. Viewport coords → position: fixed.
  // `box` is the menu's own measured size, known only on the second pass (see below) — its WIDTH
  // matters as much as its height: the clamp has to reserve the real width, and `.menu` has no
  // `max-width`, so a menu wider than MIN_WIDTH would otherwise still overflow the start edge (most
  // reachable in RTL, where a row's trigger sits near the physical left edge).
  const place = (box?: { width: number; height: number }) => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const viewport = windowViewport();
    const width = box?.width ?? MIN_WIDTH;
    // A row menu is aligned to the trigger's trailing edge (the trigger sits at the row's own trailing edge);
    // a LABELLED picker aligns to the trigger's leading edge instead, or the menu hangs off to the side of the
    // control it belongs to — which is what made the Files filters open beside their buttons. Both go through
    // the same clamp, so neither can leave the viewport.
    const resolvedAlign = align ?? (label ? 'start' : 'end');
    const inlineEnd = resolvedAlign === 'start' ? viewport.width - r.left - width : viewport.width - r.right;
    // Vertical anchor: the trigger by default, or a caller-supplied element (the chat header) whose
    // bottom the menu hangs from flush — see `blockAnchorRef`. Horizontal alignment stays trigger-based.
    const anchor = blockAnchorRef?.current;
    const blockRect = anchor ? anchor.getBoundingClientRect() : r;
    const blockGap = anchor ? blockAnchorGap : GAP;
    setPos({
      ...placeBlock(blockRect, box?.height, viewport, blockGap, EDGE),
      right: clampInlineEnd(inlineEnd, width, viewport, EDGE),
    });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  // Second pass, once the menu exists and its size is real. A trigger low in the window — the last row
  // of a Files table, a pack content row near the fold — used to drop the menu's tail off the bottom of
  // the screen with no way to reach it, because scrolling CLOSES the menu (`onReflow`). Measured rather
  // than guessed: both dimensions are the item count × the locale's label lengths and line wrapping.
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const { width, height } = menuRef.current.getBoundingClientRect();
    place({ width, height });
    // Runs once the portal has mounted; `pos` is set by the pass above, and re-running on it would
    // loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pos === null]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    // Escape goes through the SHARED layer stack (`lib/modal-escape.ts`), exactly as `Modal` does, rather
    // than a bubble-phase listener of our own. Two things follow, and both matter:
    //   - an open modal ABOVE this menu (the document viewer this menu can launch) is pushed later, so it
    //     is the innermost layer and Escape closes IT — the menu can never swallow the key from it;
    //   - the key is consumed (`stopImmediatePropagation`), so dismissing the menu does not ALSO reach the
    //     bubble-phase Escape listeners underneath, which would close the file side panel and strip
    //     `?file=` from the URL in the same keystroke.
    const release = pushModalEscape(() => setOpen(false));
    const onKey = (e: KeyboardEvent) => {
      handleModalEscape(e);
    };
    // A scroll or resize invalidates the fixed position — close rather than let it drift.
    const onReflow = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      release();
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open]);

  // Rows are often links; the trigger must act on the row, not navigate it.
  const toggle = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((v) => {
      if (!v) onOpen?.();
      return !v;
    });
  };

  // Resets one axis WITHOUT opening the menu — the whole point of the ✕. It also stops the click
  // reaching the row/link a chip may sit inside, exactly as `toggle` does.
  const clear = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    onClear?.();
  };

  // The picker chip is 36px (Figma's filter row), the bare 3-dot trigger 30px, unless the call site
  // says otherwise.
  const size = triggerSize ?? (label ? 'medium' : 'small');

  const compact = density === 'compact';

  return (
    <div className={className ? `${styles.wrap} ${className}` : styles.wrap} ref={triggerRef}>
      {label ? (
        onClear ? (
          /* A chip that is actually FILTERED: label · ✕ · chevron, in the order the Figma draws them,
             inside ONE bordered shell. Three DS controls rather than one — a ✕ nested inside the trigger
             button would be invalid HTML and would swallow the trigger's own click, and hanging the ✕
             off the chevron's hit-test by position is not reachable from a keyboard. */
          <span className={triggerClassName ? `${styles.chip} ${triggerClassName}` : styles.chip}>
            <Button
              variant="text"
              size={size}
              className={styles.chipLabel}
              aria-label={ariaLabel}
              aria-haspopup="menu"
              aria-expanded={open}
              startIcon={labelIcon}
              onClick={toggle}
            >
              {label}
            </Button>
            {/* The two trailing marks are their own GROUP: Figma sets them 4 apart and the pair 10 from
                the label, which a single flat gap on the shell cannot say. */}
            <span className={styles.chipActions}>
              <IconButton
                size="tiny"
                className={styles.chipAction}
                aria-label={clearAriaLabel ?? ariaLabel}
                onClick={clear}
              >
                <X size={16} aria-hidden />
              </IconButton>
              {/* The chevron opens the same menu the label does. It is out of the tab order and hidden
                  from AT because the label button beside it already IS the menu's accessible trigger —
                  two announcements of one control read as two controls.
                  `inert` is the WRONG tool here (it would block the very click this exists for), so the
                  remaining way focus could land inside the aria-hidden control is the pointer: a mouse
                  press focuses a button element, and the browser then finds focus inside an aria-hidden
                  subtree and drops the aria-hidden. Cancelling the default of mousedown keeps the click
                  and its `onClick` while never moving focus here — the label button keeps it. */}
              <IconButton
                size="tiny"
                className={styles.chipAction}
                aria-label={ariaLabel}
                aria-hidden
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={toggle}
              >
                <ChevronDown size={16} aria-hidden />
              </IconButton>
            </span>
          </span>
        ) : (
          <Button
            variant={triggerVariant}
            size={size}
            className={triggerClassName}
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            startIcon={labelIcon}
            endIcon={<ChevronDown size={16} aria-hidden />}
            onClick={toggle}
          >
            {label}
          </Button>
        )
      ) : (
        <IconButton
          size={size}
          className={triggerClassName}
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
        >
          {triggerIcon ?? <MoreVertical size={18} aria-hidden />}
        </IconButton>
      )}
      {open &&
        pos &&
        createPortal(
          <div
            className={[compact ? `${styles.menu} ${styles.menuCompact}` : styles.menu, menuClassName].filter(Boolean).join(' ')}
            role="menu"
            aria-label={ariaLabel}
            ref={menuRef}
            /* ⚠️ A PORTAL DOES NOT ESCAPE REACT'S EVENT BUBBLING. The menu renders into `document.body`, but a
               click inside it still bubbles up the REACT tree — to the row that opened it. Rows in this app are
               links that open the file, so choosing "Move to…" opened the viewer as well and the dialog was
               found waiting underneath it once you closed the file you never asked to see.
               Stopping it here fixes every host at once; doing it per action is how one gets forgotten. */
            onClick={(e) => e.stopPropagation()}
            style={{ top: pos.top ?? 'auto', bottom: pos.bottom ?? 'auto', right: pos.right }}
          >
            {(groups ?? [actions ?? []]).map((group, groupIndex) => (
              <div key={groupIndex} className={compact ? styles.groupCompact : undefined}>
                {groupIndex > 0 && (
                  <hr
                    className={compact ? `${styles.divider} ${styles.dividerCompact}` : styles.divider}
                    role="separator"
                  />
                )}
                {group.map((a) => {
                  const base = a.destructive ? styles.itemDanger : styles.item;
                  const className = compact ? `${base} ${styles.itemCompact}` : base;
                  const content = (
                    <>
                      {a.icon && <span className={styles.itemIcon}>{a.icon}</span>}
                      <span className={styles.itemLabel}>{a.label}</span>
                      {selectedKey === a.key && (
                        <span className={styles.itemCheck}>
                          <Check size={16} aria-hidden />
                        </span>
                      )}
                    </>
                  );
                  if (a.href) {
                    return (
                      <a
                        key={a.key}
                        role="menuitem"
                        className={className}
                        href={a.href}
                        target={a.external || a.newTab ? '_blank' : undefined}
                        rel={a.external || a.newTab ? 'noopener noreferrer' : undefined}
                        onClick={() => {
                          setOpen(false);
                          a.onSelect?.();
                        }}
                      >
                        {content}
                      </a>
                    );
                  }
                  return (
                    <button
                      key={a.key}
                      type="button"
                      role={selectedKey === undefined ? 'menuitem' : 'menuitemradio'}
                      aria-checked={selectedKey === undefined ? undefined : selectedKey === a.key}
                      className={className}
                      onClick={() => {
                        setOpen(false);
                        a.onSelect?.();
                      }}
                    >
                      {content}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
